import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FaCalendarAlt, FaDownload, FaEye } from "react-icons/fa";
import styles from "../FeeType/styles.module.css";
import { CustomButton, CustomDropdown } from "../../../../components";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import PopupModal from "../../../../components/PopupModal/PopupModal";
import { useSelector, useDispatch } from "react-redux";
import {
  clearSectionOptions,
  fetchClassOptions,
  fetchSectionOptions,
} from "../../../../redux/Masterdata";


const AssignedStudentsBadge = ({ assigned, total }) => {
  // `assigned` here receives the UNASSIGNED count
  const a = Number(assigned ?? 0);
  const t = Number(total ?? 0);

  let bg = "#dc2626";            // unassigned students remain -> red
  if (t === 0) bg = "#9ca3af";   // no students -> gray
  else if (a === 0) bg = "#16a34a"; // 0 unassigned = fully assigned -> green

  return (
    <span
      style={{
        display: "inline-block",
        minWidth: 52,
        textAlign: "center",
        padding: "4px 12px",
        borderRadius: 6,
        fontWeight: 700,
        color: "#fff",
        backgroundColor: bg,
      }}
    >
      {a}/{t}
    </span>
  );
};

const ListFeeAssign = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { translations } = useSelector((state) => state.language);

  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";
  // Payload uses `academic_id`
  const academic_id =
    clientData?.academic_id || clientData?.academic_year_id || "";

  const [data, setData] = useState([]);

  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [apiSearch, setApiSearch] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [modalState, setModalState] = useState({
    open: false,
    type: "",
    message: "",
    redirectPath: "",
  });
  // ── Class / Section filter state ──
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");

  const {
    classOptions: reduxClassOptions,
    staffClassAndSubjectOptions,
    sectionOptions: reduxSectionOptions,
  } = useSelector((state) => state.masterData);

  const roleId = Number(user?.role?.id);
  const master_id = clientData?.master_id || "";
  const academic_year = clientData?.academic_year || "";

  // ── Fetch class options on mount ──
  useEffect(() => {
    if (school_id) {
      dispatch(
        fetchClassOptions({
          id: parseInt(school_id),
          academic_year: academic_year,
        }),
      );
    }
  }, [dispatch, school_id]);

  const classOptions = useMemo(() => {
    const sourceData =
      roleId === 3 ? staffClassAndSubjectOptions : reduxClassOptions;
    return [
      {
        value: "",
        label: translations.labels?.selectClass || "Select Class",
      },
      ...sourceData.map((item) => ({
        value: item.class_id ?? item.value,
        label: item.class_name ?? item.label,
      })),
    ];
  }, [reduxClassOptions, staffClassAndSubjectOptions, roleId, translations]);

  const sectionOptions = useMemo(
    () => [
      {
        value: "",
        label: translations.labels?.selectSection || "Select Section",
      },
      ...reduxSectionOptions,
    ],
    [reduxSectionOptions, translations],
  );

  const handleClassChange = useCallback(
    (value) => {
      const classId = typeof value === "object" ? value?.value : value;
      setSelectedClass(classId);
      setSelectedSection("");
      dispatch(clearSectionOptions());
      if (classId) {
        dispatch(
          fetchSectionOptions({
            id: [parseInt(classId)],
            school_id: parseInt(school_id),
            staff_id: parseInt(master_id),
            subject_staff: 1,
          }),
        );
      }
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [dispatch, school_id],
  );

  const handleSectionChange = useCallback((value) => {
    const sectionId = typeof value === "object" ? value?.value : value;
    setSelectedSection(sectionId);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  useEffect(() => {
    if (school_id) {
      fetchAssignedClasses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    school_id,
    pagination.pageIndex,
    pagination.pageSize,
    apiSearch,
    selectedClass,
    selectedSection,
  ]);

  const fetchAssignedClasses = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.post('schoolFees/group/assigned-classess', {
        school_id: school_id,
        academic_id: academic_id,
        class_id: selectedClass ? parseInt(selectedClass) : "",
        section_id: selectedSection || "",
        search: apiSearch || "",
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
      });

      // New API returns `success` + `pagination` object
      if (response?.data?.success) {
        const list = response.data.data || [];
        const transformed = list.map((item, index) => ({
          s_no: pagination.pageIndex * pagination.pageSize + index + 1,
          class_id: item.class_id,
          unassigned_students: item.unassigned_students,
          section_id: item.section_id,
          class: item.class_name || "",
          section: item.section_id || "",
          medium: item.medium || "",
          total_students: item.total_students ?? 0,
          assigned_students: item.assigned_students ?? 0,
        }));
        setData(transformed);
        setRowCount(response.data.pagination?.total || transformed.length);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error("Error fetching assigned classes:", error);
      setData([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchEnter = useCallback((value) => {
    setApiSearch(value || "");
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const handleAdd = () => {
    navigate("/user/createFeeAssign");
  };

  const handleView = (row) => {
    navigate("/user/createFeeAssign", {
      state: {
        data: row,
        mode: "view",
        class_id: row.class_id,
        class_name: row.class,
        section_id: row.section_id,
      },
    });
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "s_no",
        header: translations.labels.serialNo,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "class",
        header: translations.labels.class,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "section",
        header: translations.labels.section,
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      // {
      //     accessorKey: "medium",
      //     header: translations.labels.medium || "Medium",
      //     enableSorting: true,
      //     enableColumnFilter: false,
      // },
      {
        accessorKey: "unassigned_students",
        header: translations.labels.unAssignStudents || "Unassigned Students",
        enableSorting: true,
        enableColumnFilter: false,
        // sort by how many are assigned; display as assigned/total pill
        Cell: ({ row }) => (
          <AssignedStudentsBadge
            assigned={row.original.unassigned_students}
            total={row.original.total_students}
          />
        ),
      },
      {
        id: "actions",
        header: translations.labels.actions,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <button
            className={styles.viewBtn}
            onClick={() => handleView(row.original)}
            title="View"
          >
            <FaEye />
          </button>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [translations],
  );

  /** Convert array of objects to CSV string */
  const convertToCSV = (rows, headerMap) => {
    if (!rows || rows.length === 0) return "";

    const keys = Object.keys(headerMap);
    const headerRow = keys.map((k) => `"${headerMap[k]}"`).join(",");

    const body = rows.map((row) =>
      keys
        .map((key) => {
          const val = row[key] ?? "";
          const escaped = String(val).replace(/"/g, '""');
          return `"${escaped}"`;
        })
        .join(","),
    );

    return [headerRow, ...body].join("\n");
  };

  /** Trigger CSV file download in the browser */
  const downloadCSV = (csvContent, filename) => {
    const blob = new Blob(["\uFEFF" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  /** Download all assigned-class records as CSV (loops through every page) */
  const handleDownload = async () => {
    try {
      setDownloadLoading(true);

      let allData = [];
      let page = 1;
      let totalPages = 1;

      do {
        const response = await AxiosInstance.post('schoolFees/group/assigned-classess', {
          school_id: school_id,
          academic_id: academic_id,
          class_id: selectedClass ? parseInt(selectedClass) : "",
          section_id: selectedSection || "",
          page: page,
          limit: 100,
        });

        if (response?.data?.success) {
          allData = [...allData, ...(response.data.data || [])];
          totalPages = response.data.pagination?.total_pages || 1;
        } else {
          break;
        }
        page++;
      } while (page <= totalPages);

      if (allData.length === 0) {
        setModalState({
          open: true,
          type: "error",
          message: "No data available to download.",
          redirectPath: "",
        });
        return;
      }

      const exportRows = allData.map((item, index) => ({
        s_no: index + 1,
        class: item.class_name || "",
        section: item.section_id || "",
        medium: item.medium || "",
        assigned_students: item.assigned_students ?? 0,
        total_students: item.total_students ?? 0,
      }));

      const csvContent = convertToCSV(exportRows, {
        s_no: "S.No",
        class: "Class",
        section: "Section",
        medium: "Medium",
        assigned_students: "Assigned Students",
        total_students: "Total Students",
      });

      downloadCSV(csvContent, "ASSIGNED_CLASSES_LIST.csv");
    } catch (error) {
      console.error("Error downloading assigned classes list:", error);
      setModalState({
        open: true,
        type: "error",
        message: "Failed to download assigned classes list. Please try again.",
        redirectPath: "",
      });
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <PopupModal
        open={modalState.open}
        title={
          modalState.type === "success"
            ? translations.labels?.success || "Success"
            : translations.labels?.failed || "Failed"
        }
        message={modalState.message}
        onOk={() => setModalState((prev) => ({ ...prev, open: false }))}
      />

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FaCalendarAlt />
        </div>
        <h1 className={styles.headerTitle}>{translations.labels.feeAssign}</h1>
      </div>

      {/* ── Filters ── */}
      <div className="row" style={{ marginBottom: 16 }}>
        <div className="col-lg-3 px-2 col-md-6 col-sm-12">
          <CustomDropdown
            label={translations.labels?.class || "Class"}
            name="filterClass"
            value={selectedClass}
            onChange={handleClassChange}
            options={classOptions}
            placeholder={translations.labels?.selectClass || "Select Class"}
          />
        </div>
        <div className="col-lg-3 px-2 col-md-6 col-sm-12">
          <CustomDropdown
            label={translations.labels?.section || "Section"}
            name="filterSection"
            value={selectedSection}
            onChange={handleSectionChange}
            options={sectionOptions}
            placeholder={translations.labels?.selectSection || "Select Section"}
            disabled={!selectedClass}
          />
        </div>
      </div>

      {/* Actions */}
      <div className={styles.actions}>
        <CustomButton
          type="button"
          variant="contained"
          className={styles.addButton}
          fullWidth={false}
          onClick={handleAdd}
        >
          {translations.labels.add}
        </CustomButton>

        <CustomButton
          type="button"
          variant="contained"
          className={`${styles.downloadButton} `}
          fullWidth={false}
          onClick={handleDownload}
          disabled={downloadLoading}
        >
          <FaDownload style={{ marginRight: "8px" }} />
          {downloadLoading
            ? "Downloading..."
            : translations.labels?.downloadButton || "Download"}
        </CustomButton>
      </div>

      {/* Table */}
      <CustomMRTTable
        columns={columns}
        data={data}
        loading={loading}
        pagination={pagination}
        rowCount={rowCount}
        placeholder={
          translations?.labels?.searchClassSection || "Search Class & Section"
        }
        setSearchInput={setSearchInput}
        onSearchEnter={handleSearchEnter}
        onPaginationChange={setPagination}
        searchInput={searchInput}
        enableSearch={true}
        enableFiltering={false}
        manualPagination={true}
      />
    </div>
  );
};

export default ListFeeAssign;
